import { Injectable, computed, signal } from '@angular/core';
import { Artifact } from '../models/artifact.model';

/**
 * The single piece of state shared between the chat (which produces artifact
 * cards) and the report panel (which renders them). A root singleton so any
 * component — the chat, the pcheck renderer, a future agent renderer — can open
 * the same panel without wiring inputs/outputs through the tree.
 *
 * `close()` deliberately keeps `activeArtifact` set so the panel can be
 * re-opened in place; the in-chat card re-opens it via `open()`.
 */
@Injectable({ providedIn: 'root' })
export class ArtifactService {
  /** Currently selected artifact (kept even while the panel is closed). */
  readonly activeArtifact = signal<Artifact | null>(null);
  /** Whether the panel column is visible. */
  readonly panelOpen = signal<boolean>(false);

  /** True when this artifact is the one currently shown in an open panel. */
  readonly isActive = (id: string) =>
    computed(() => this.panelOpen() && this.activeArtifact()?.id === id);

  /** Open (or switch the panel to) an artifact. */
  open(artifact: Artifact): void {
    this.activeArtifact.set(artifact);
    this.panelOpen.set(true);
  }

  /** Hide the panel without forgetting the selection. */
  close(): void {
    this.panelOpen.set(false);
  }

  /** Forget everything — call when switching sessions or starting a new chat so
   *  a report from the previous conversation can't linger in the panel. */
  reset(): void {
    this.activeArtifact.set(null);
    this.panelOpen.set(false);
  }

  /** Open if closed/different, close if this same artifact is already showing. */
  toggle(artifact: Artifact): void {
    if (this.panelOpen() && this.activeArtifact()?.id === artifact.id) {
      this.close();
    } else {
      this.open(artifact);
    }
  }

  /** Open the report in a new browser tab. */
  openInNewTab(artifact: Artifact | null): void {
    if (artifact?.fileUrl) window.open(artifact.fileUrl, '_blank', 'noopener');
  }

  /**
   * Trigger a real file download (not a tab redirect). Fetches the report as a
   * blob and saves it via an object URL so the browser writes a file instead of
   * navigating — the `download` attribute is ignored for cross-origin URLs like
   * S3 presigned links, which is why a plain anchor opened a tab. Falls back to
   * opening a new tab if the fetch is blocked (e.g. bucket CORS not configured).
   */
  async download(artifact: Artifact | null): Promise<void> {
    const url = artifact?.fileUrl;
    if (!url) return;
    // Stable report links (/email-link/{hash}) 302 to a presigned S3 URL. The
    // fetch below can't follow that cross-origin (no CORS on the redirect or the
    // bucket), so it used to always fall through to a new tab. Ask the link
    // handler for an attachment instead. It is loaded in a hidden iframe, not via
    // a top-level navigation: the attachment response triggers the save without
    // any visible tab, and an error response (404/410/500 — expired link, missing
    // record) renders inside the invisible frame instead of navigating the app
    // away and losing the chat session.
    if (/\/email-link\/[^/?#]+/.test(url)) {
      const frame = document.createElement('iframe');
      frame.style.display = 'none';
      frame.setAttribute('aria-hidden', 'true');
      frame.src = this._withDownloadFlag(url);
      document.body.appendChild(frame);
      setTimeout(() => frame.remove(), 60_000);
      return;
    }
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = artifact!.fileName || this._inferFileName(artifact!, blob.type);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // CORS/network failure — let the user save manually from a new tab.
      window.open(url, '_blank', 'noopener');
    }
  }

  private _withDownloadFlag(url: string): string {
    const u = new URL(url, window.location.href);
    u.searchParams.set('download', '1');
    return u.toString();
  }

  /** Best-effort download filename when the artifact didn't supply one. */
  private _inferFileName(artifact: Artifact, mime: string): string {
    const base = (artifact.title || 'report').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const ext = mime.includes('pdf') ? '.pdf' : mime.includes('html') ? '.html' : '';
    return `${base}${ext}`;
  }
}
